import {ReactNode, useState} from "react";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import StringInputLabel from "../../elements/stringInputLabel.element";
import {StartingUnitCommand} from "../../commands/startingUnit.command";
import {StartingBuildingCommand} from "../../commands/startingBuilding.command";
import {useIntegrator} from "../../integrator.context";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import {NoiseMap} from "../../models/remoteConstants.model";
import CombatSimulationView from "./combatSimulation.view";
import SelectableLabel from "../../elements/selectable.label";
import Collapsible from "../../elements/collapsible";
import {formulaStatus, SCORE_KIND_LABEL, ScoreKind, scoreVariablesFor} from "../../utils/scoreFormula";

// One collapsible group of General settings.
//
// Driven in Collapsible's CONTROLLED mode purely so the whole header toggles, not just
// the caret: Collapsible self-toggles only on clicks that land on the header background
// (`e.target === e.currentTarget`), which a styled <p> title would swallow. The caret
// stops propagation and toggles on its own, so there is no double-toggle.
//
// Open by default, so the page reads exactly as it did before — the sections are there to
// let a designer fold away what they aren't tuning, not to hide anything by default.
function Section(props: { title: string, children: ReactNode }) {
    const [open, setOpen] = useState(true);

    return (
        <Collapsible open={open} onOpenChange={setOpen}
                     title={<p className={'text header'} style={{cursor: 'pointer'}}
                               onClick={() => setOpen(!open)}>{props.title}</p>}>
            <div className={'layout vertical gap'} style={{marginTop: 10}}>
                {props.children}
            </div>
        </Collapsible>
    );
}

// One per-kind Score Formula (what ONE unit / building / city / tech is worth): the input
// and, when the game would reject the formula, the warning that says so. The Score fields
// themselves are never written from here — each shows its in-game value beside it instead.
function ScoreFormulaRow(props: { kind: ScoreKind, field: string, value: string, onChange(value: string): void }) {
    const status = formulaStatus(props.value, scoreVariablesFor(props.kind));
    const variables = scoreVariablesFor(props.kind).join(', ');
    return (
        <div className={'layout vertical'} style={{gap: 4}}>
            <StringInputLabel field={props.field} value={props.value} placeholder={'SCORE'} onChange={props.onChange}/>
            {status === 'invalid' &&
                <p style={{color: '#ff6666', fontSize: 12}}>
                    <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                    {`Invalid formula: every ${SCORE_KIND_LABEL[props.kind].toLowerCase()} is worth 0 in game until it is fixed. Use ${variables}, numbers, + - * / and parentheses.`}
                </p>}
        </div>
    );
}

function GeneralEditorView() {
    const {state, executeCommand} = useIntegrator();

    //set default values so they'll be added to the remoteconstantsmodel
    state.elevationNoise ??= {} as NoiseMap;
    state.temperatureNoise ??= {} as NoiseMap;
    state.goldScore ??= 5;
    state.goldPerTurnScore ??= 20;
    state.tileInTerritoryScore ??= 15;
    state.tileFullVisionScore ??= 10;
    state.tileFogVisionScore ??= 5;

    return (
        // minHeight: 0 is what makes the two columns' own scrolling work. Without
        // it this row's height comes from its content, so neither the settings
        // column's overflowY below nor the Simulations list's scroller inside
        // CombatSimulation ever engages — the whole tab scrolls in the pane
        // wrapper instead, taking Combat Simulation with it. (`flex: 1` sets the
        // basis on the horizontal main axis here, so it does NOT bound height.)
        <div className={'layout horizontal box flex-grow stretch'} style={{gap: 10, minHeight: 0}}>
            <div className={'layout vertical gap'} style={{flex: 1, minWidth: 300, gap: 20, overflowY: 'auto'}}>
                <Section title={'Starting'}>
                    <SelectableLabel field={'general.startingUnit'} value={state.startingUnit} options={state.units.map((b) => b.id)}
                                onChange={value => executeCommand(new StartingUnitCommand(value))}/>
                    <SelectableLabel field={'general.startingBuilding'} value={state.startingBuilding}
                                options={state.buildings.map((b) => b.id)}
                                onChange={value => executeCommand(new StartingBuildingCommand(value))}/>
                </Section>

                <Section title={'Gold'}>
                    <NumberInputLabel field={'general.startingGold'} value={state.startingGold}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.startingGold = v))}/>
                    <NumberInputLabel field={'general.capitalCityGoldPerTurn'} value={state.capitalCityGoldPerTurn}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.capitalCityGoldPerTurn = v))}/>
                    <NumberInputLabel field={'general.cityGoldPerTurn'} value={state.cityGoldPerTurn}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.cityGoldPerTurn = v))}/>
                </Section>

                <Section title={'Misc'}>
                    <NumberInputLabel field={'general.turnsToFog'} value={state.turnsToFog ?? 0}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.turnsToFog = v))}/>
                    {/* Connection Bonus: absent from branches authored before it existed, so
                        default to 0 — matching the client, and meaning "off" until authored. */}
                    <NumberInputLabel field={'general.connectionBonusCityPopulation'} value={state.connectionBonusCityPopulation ?? 0}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.connectionBonusCityPopulation = v))}/>
                    <NumberInputLabel field={'general.connectionBonusCapitalPopulation'} value={state.connectionBonusCapitalPopulation ?? 0}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.connectionBonusCapitalPopulation = v))}/>
                </Section>

                <Section title={'Combat'}>
                    <StringInputLabel field={'general.attackDamageFormula'} value={state.attackDamageFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.attackDamageFormula = v))}/>
                    <NumberInputLabel field={'general.minimumDamage'} value={state.minimumDamage ?? 1}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.minimumDamage = v))}/>
                    <StringInputLabel field={'general.retaliatoryDamageFormula'} value={state.retaliatoryDamageFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.retaliatoryDamageFormula = v))}/>
                    <NumberInputLabel field={'general.minimumRetaliatoryDamage'} value={state.minimumRetaliatoryDamage ?? 1}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.minimumRetaliatoryDamage = v))}/>
                    <NumberInputLabel field={'general.criticalHealthPercent'} value={state.criticalHealthPercent ?? 50}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.criticalHealthPercent = v))}/>
                    <NumberInputLabel field={'general.criticalAttackPenalty'} value={state.criticalAttackPenalty ?? 1}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.criticalAttackPenalty = v))}/>
                    <NumberInputLabel field={'general.criticalDefensePenalty'} value={state.criticalDefensePenalty ?? 1}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.criticalDefensePenalty = v))}/>
                </Section>

                <Section title={'Score'}>
                    <StringInputLabel field={'general.playerScoreFormula'} value={state.playerScoreFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.playerScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'unit'} field={'general.unitScoreFormula'} value={state.unitScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.unitScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'building'} field={'general.buildingScoreFormula'} value={state.buildingScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.buildingScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'city'} field={'general.cityScoreFormula'} value={state.cityScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.cityScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'tech'} field={'general.techScoreFormula'} value={state.techScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.techScoreFormula = v))}/>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        Each of the four formulas is what ONE unit, building, city (Starting Slots &gt; 0) or tech is worth,
                        and is the source of truth for its kind. SCORE = the entity's Player Score field; COST = its gold price
                        (units, buildings, techs); TURNS = a tier's research turns (techs); LEVEL = a city's current level (cities,
                        which are founded rather than bought). Blank = SCORE, i.e. the Player Score fields alone.
                        The fields are never rewritten: each shows its "Score by Formula" underneath.
                    </p>
                    <NumberInputLabel field={'general.goldScore'} value={state.goldScore ?? 5}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.goldScore = v))}/>
                    <NumberInputLabel field={'general.goldPerTurnScore'} value={state.goldPerTurnScore ?? 20}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.goldPerTurnScore = v))}/>
                    <NumberInputLabel field={'general.tileInTerritoryScore'} value={state.tileInTerritoryScore ?? 15}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.tileInTerritoryScore = v))}/>
                    <NumberInputLabel field={'general.tileFullVisionScore'} value={state.tileFullVisionScore ?? 10}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.tileFullVisionScore = v))}/>
                    <NumberInputLabel field={'general.tileFogVisionScore'} value={state.tileFogVisionScore ?? 5}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.tileFogVisionScore = v))}/>
                </Section>
                <Section title={'Elevation Noise Map'}>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.scale'} value={state.elevationNoise.scale}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.scale = v))}/>
                    <NumberInputLabel field={'general.elevationNoise.octaves'} value={state.elevationNoise.octaves}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.octaves = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.lacunarity'} value={state.elevationNoise.lacunarity}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.lacunarity = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.persistance'} value={state.elevationNoise.persistance}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.persistance = v))}/>
                </Section>
                <Section title={'Temperature Noise Map'}>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.scale'} value={state.temperatureNoise.scale}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.scale = v))}/>
                    <NumberInputLabel field={'general.temperatureNoise.octaves'} value={state.temperatureNoise.octaves}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.octaves = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.lacunarity'} value={state.temperatureNoise.lacunarity}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.lacunarity = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.persistance'} value={state.temperatureNoise.persistance}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.persistance = v))}/>
                </Section>
            </div>
            <CombatSimulationView/>
        </div>
    )
}

export default GeneralEditorView;